<template>
  <v-card flat>
    <transition name="fade">
      <v-alert
        v-if="!readonly && !isConfirmingTabChange"
        type="info"
        dense
        prominent
        border="left"
        icon="mdi-lock-alert-outline"
        class="sticky-edit-banner"
        :color="environmentColor"
      >
        <span v-html="editModeExitMessage"></span>
      </v-alert>
    </transition>
    <v-card-title>
      <v-row>
        <v-col cols="6" class="text-h5 text-break">
          {{ esvReference }} {{ esv.subject }}
        </v-col>
        <v-col>
          <span class="d-flex justify-end">
            <slot name="append-title"></slot>
          </span>
        </v-col>
      </v-row>
    </v-card-title>
    <v-card-text>
      <v-divider class="mb-4"></v-divider>

      <div class="d-flex justify-space-between">
        <span>
          <span v-if="esv.lockedByFullName" class="text-subtitle-1 yellow lighten-2">
            ESV is currently edited by {{ esv.lockedByFullName }}
          </span>
          <v-btn
              class="mr-2"
              color="primary"
              small
              v-else-if="$can(action, esv)"
              @click="$emit('edit')"
              :loading="lockLoading"
          >
            <v-icon left>{{ readonly ? 'mdi-pencil' : 'mdi-arrow-left' }}</v-icon>
            {{ readonly ? 'Edit current item' : 'Leave edit mode' }}
          </v-btn>
          <slot name="secondary-actions">
            <v-btn outlined small @click="routeBackToList">
              <v-icon left>mdi-arrow-left</v-icon>Back
            </v-btn>
          </slot>
        </span>
        <slot name="append-actions"></slot>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import common from '@/services/Common';
import {FieldStatus} from "@/enums/FieldStatus";
import {DocumentFields} from "@/enums/DocumentFields";

export default {
  name: 'TabHeader',
  props: {
    esv: {
      type: Object,
      required: true
    },
    readonly: {
      type: Boolean,
      default: true
    },
    action: {
      type: String,
      default: 'Edit'
    },
    lockLoading: {
      type: Boolean,
      default: false
    },
    isConfirmingTabChange: {
      type: Boolean,
      default: false
    },
    editModeExitMessage: {
        type: String,
        default: '<strong>Editing Mode:</strong> This document is locked for you. Please use <strong>Save</strong> or <strong>Cancel</strong> below to finish and release the lock for others.'
    }
  },
  computed: {
    esvReference() {
      if (!this.esv) return '';

      let reference = '';
      if (typeof this.esv.serviceRequestType === 'object') {
        reference = this.esv.serviceRequestType?.text || '';
      } else {
        reference = this.esv.serviceRequestType || '';
      }

      if (this.esv.dossier) {
        reference += this.esv.dossier;
      }

      const versionStatus = common.getFieldStatus(this.$store, this.esv, DocumentFields.ServiceRequestNrVersion);
      const hasVersion = this.esv.version && FieldStatus.isEnabled(versionStatus);
      const hasRevision = this.esv.revision;

      if (hasVersion) {
        reference += '-';
      }

      if (hasVersion) {
        reference += this.esv.version;
      }

      if (hasRevision) {
        reference += this.esv.revision;
      }

      return reference;
    },
    environmentColor() {
      // Use the environment color from UI settings, fallback to 'primary'
      return this.$store?.state?.uiSettings?.color ?? 'primary';
    }
  },
  methods: {
    routeBackToList() {
      if (!this.readonly) {
        this.$emit('edit');
      }
      this.$router.go(-1);
    }
  }
}
</script>

<style scoped>
.sticky-edit-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  padding-left: 16px;
  padding-right: 16px;
  text-align: center;
}

/* Fade transition */
.fade-enter-active {
  transition: opacity 0.3s ease;
  transition-delay: 0.5s; /* Add delay only for enter */
}

.fade-leave-active {
  transition: opacity 0.3s ease; /* No delay for leave */
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}
</style>
